// src/services/arAudioService.js

/**
 * AR Audio Service
 *
 * Uses Web Audio API to play a short “arrival” ping.
 * Designed to be unlocked via a user gesture, to avoid autoplay restrictions.
 */

let audioContext = null;
let unlocked = false;

function getAudioContextSafe() {
  if (audioContext) return audioContext;

  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;

  audioContext = new AC();
  return audioContext;
}

export async function unlockArAudio() {
  if (typeof window === "undefined") return { ok: false, reason: "no_window" };

  const ctx = getAudioContextSafe();
  if (!ctx) return { ok: false, reason: "unsupported" };

  try {
    if (ctx.state === "suspended") {
      await ctx.resume();
    }
    unlocked = true;
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: e instanceof Error ? e.message : "resume_failed" };
  }
}

function playBeepInternal({ frequencyHz, durationMs, gain }) {
  const ctx = getAudioContextSafe();
  if (!ctx || !unlocked) return false;

  try {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();

    osc.type = "sine";
    osc.frequency.value = frequencyHz;

    // Soft envelope to avoid clicking
    const now = ctx.currentTime;
    const dur = Math.max(0.02, durationMs / 1000);
    const peak = Math.max(0.0001, Math.min(0.3, gain));

    g.gain.setValueAtTime(0.0001, now);
    g.gain.linearRampToValueAtTime(peak, now + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, now + dur);

    osc.connect(g);
    g.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + dur + 0.02);

    return true;
  } catch {
    return false;
  }
}

export function playArrivalPing() {
  // Two-tone ping
  const ok1 = playBeepInternal({ frequencyHz: 880, durationMs: 110, gain: 0.15 });
  const ok2 = playBeepInternal({ frequencyHz: 1175, durationMs: 140, gain: 0.13 });
  return ok1 || ok2;
}
